import React, { useState, useEffect } from "react";
import Layout from "../layout";
import { useAuth } from "../authContext";
import {
  Brain,
  Award,
  Star,
  Clock,
  Zap,
  Shield,
  CheckCircle,
  ArrowLeft,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import StressAssessmentDetail from "../assessments/stress/StressAssessmentDetail";
import AnxietyAssessmentDetail from "../assessments/anxiety/AnxietyAssessmentDetail";
import WellbeingAssessmentDetail from "../assessments/wellness/WellbeingAssessmentDetail";
import IQAssessmentDetail from "../assessments/iq/IQAssessmentDetail";
import { mentalHealthAssessments, badges } from "./assessmentdata";

const StandaloneAssessment = () => {
  const { url, headers } = useAuth();
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [completedAssessments, setCompletedAssessments] = useState({});
  const [currentView, setCurrentView] = useState("selection");
  const [pastScores, setPastScores] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchPastScores = async () => {
    if (!headers) return;

    setLoading(true);
    try {
      const response = await fetch(`${url}/assessment`, {
        method: "GET",
        headers: headers,
      });

      if (response.ok) {
        const apiResponse = await response.json();
        const data = apiResponse.data;
        setPastScores(data.assessments || []);
        //console.log('✅ Past scores fetched successfully:', data);
      } else {
        console.error("❌ Failed to fetch past scores:", response.status);
      }
    } catch (error) {
      console.error("❌ Error fetching past scores:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPastScores();
  }, [headers]);

  const handleStartAssessment = (assessment) => {
    setSelectedAssessment(assessment);
    setCurrentView("assessment-detail");
  };

  const saveAssessmentResults = async (assessmentTitle, score) => {
    try {
      const response = await fetch(`${url}/assessment`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          test_name: assessmentTitle,
          score: score,
        }),
      });

      if (response.ok) {
        //console.log('✅ Assessment results saved successfully');
        return await response.json();
      } else {
        console.error("❌ Failed to save assessment results:", response.status);
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error("❌ Error saving assessment results:", error);
      return null;
    }
  };

  const handleAssessmentComplete = async (assessmentId, results) => {
    setCompletedAssessments((prev) => ({
      ...prev,
      [assessmentId]: results,
    }));

    const assessment = mentalHealthAssessments.find(
      (a) => a.id === assessmentId
    );

    if (assessment && results.percentage !== undefined) {
      await saveAssessmentResults(assessment.title, results.percentage);
      // Refresh past scores after saving
      await fetchPastScores();
    }
  };

  const handleBackToSelection = () => {
    setCurrentView("selection");
    setSelectedAssessment(null);
  };

  if (currentView === "assessment-detail" && selectedAssessment) {
    if (selectedAssessment.type === "iq") {
      return (
        <IQAssessmentDetail
          onBack={handleBackToSelection}
          onComplete={(results) =>
            handleAssessmentComplete(selectedAssessment.id, results)
          }
        />
      );
    }

    if (selectedAssessment.type === "stress") {
      return (
        <StressAssessmentDetail
          onBack={handleBackToSelection}
          onComplete={(results) =>
            handleAssessmentComplete(selectedAssessment.id, results)
          }
        />
      );
    }

    if (selectedAssessment.type === "anxiety") {
      return (
        <AnxietyAssessmentDetail
          onBack={handleBackToSelection}
          onComplete={(results) =>
            handleAssessmentComplete(selectedAssessment.id, results)
          }
        />
      );
    }

    if (selectedAssessment.type === "wellbeing") {
      return (
        <WellbeingAssessmentDetail
          onBack={handleBackToSelection}
          onComplete={(results) =>
            handleAssessmentComplete(selectedAssessment.id, results)
          }
        />
      );
    }
  }

  return (
    <Layout>
      <div className="min-h-screen p-6">
        <div className="max-w-8xl mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
                <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900">
                  Assessment Hub
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                  Discover insights about your mental wellness through
                  comprehensive assessments
                </p>
                <p className="text-xs text-gray-600 sm:hidden">
                  Your assessment journey
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6 sm:space-y-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl border border-white/20">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                <Brain className="w-6 h-6 sm:w-7 sm:h-7 text-purple-500" />
                Mental Health Assessments
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {mentalHealthAssessments.map((assessment, index) => {
                  const isCompleted = completedAssessments[assessment.id];

                  const pastAssessment = pastScores.find(
                    (score) =>
                      score.test_name
                        .toLowerCase()
                        .includes(
                          assessment.title.toLowerCase().split(" ")[0]
                        ) ||
                      (assessment.type === "iq" &&
                        score.test_name.toLowerCase().includes("iq")) ||
                      (assessment.type === "stress" &&
                        score.test_name.toLowerCase().includes("stress")) ||
                      (assessment.type === "anxiety" &&
                        score.test_name.toLowerCase().includes("anxiety")) ||
                      (assessment.type === "wellbeing" &&
                        (score.test_name.toLowerCase().includes("wellbeing") ||
                          score.test_name.toLowerCase().includes("wellness")))
                  );

                  const recentAssessment = pastScores
                    .filter(
                      (score) =>
                        score.test_name
                          .toLowerCase()
                          .includes(
                            assessment.title.toLowerCase().split(" ")[0]
                          ) ||
                        (assessment.type === "iq" &&
                          score.test_name.toLowerCase().includes("iq")) ||
                        (assessment.type === "stress" &&
                          score.test_name.toLowerCase().includes("stress")) ||
                        (assessment.type === "anxiety" &&
                          score.test_name.toLowerCase().includes("anxiety")) ||
                        (assessment.type === "wellbeing" &&
                          (score.test_name
                            .toLowerCase()
                            .includes("wellbeing") ||
                            score.test_name.toLowerCase().includes("wellness")))
                    )
                    .sort(
                      (a, b) => new Date(b.created_at) - new Date(a.created_at)
                    )[0];

                  const hasCompletedBefore = !!pastAssessment;

                  return (
                    <motion.div
                      key={assessment.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border transition-all relative cursor-pointer ${
                        hasCompletedBefore || isCompleted
                          ? "bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-xl hover:scale-105"
                          : "bg-gradient-to-br from-white to-gray-50 border-white/20 hover:shadow-xl hover:scale-105"
                      }`}
                      onClick={() => handleStartAssessment(assessment)}
                    >
                      <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
                        {hasCompletedBefore || isCompleted ? (
                          <>
                            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                              <CheckCircle className="w-4 h-4 text-white" />
                            </div>
                            {recentAssessment && (
                              <div className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                                {/*recentAssessment.score %*/}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                            <Brain className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>

                      <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">
                        {assessment.icon}
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-2">
                        {assessment.title}
                      </h3>
                      <p className="text-xs text-gray-600 mb-3">
                        {assessment.subtitle}
                      </p>

                      {/* Assessment Info */}
                      <div className="text-xs text-gray-500 mb-4">
                        {hasCompletedBefore || isCompleted ? (
                          <div className="space-y-1">
                            {recentAssessment ? (
                              <>
                                <div>
                                  Last taken:{" "}
                                  {new Date(
                                    recentAssessment.created_at
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </div>
                              </>
                            ) : (
                              isCompleted && (
                                <>
                                  <div>
                                    Score:{" "}
                                    {
                                      completedAssessments[assessment.id]
                                        .percentage
                                    }
                                    %
                                  </div>
                                  <div>
                                    Result:{" "}
                                    {completedAssessments[assessment.id].level}
                                  </div>
                                </>
                              )
                            )}
                          </div>
                        ) : (
                          <span className="text-blue-600">
                            🎯 Click to start assessment
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-gray-600 mb-4">
                        {assessment.description}
                      </p>

                      {isCompleted ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartAssessment(assessment);
                          }}
                          className="w-full py-2 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 transition-colors"
                        >
                          🔄 Retake Assessment
                        </button>
                      ) : (
                        <div
                          className={`w-full py-2 text-white rounded-lg text-center text-xs font-medium bg-gradient-to-r ${assessment.color}`}
                        >
                          🚀 Start Assessment
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StandaloneAssessment;
